import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntegrations } from "../lib/tenant-api";
import { apiFetch } from "../lib/api";
import {
  ErrorBanner,
  LoadingRow,
} from "../components/EmptyState";

export default function Integrations() {
  const { data, isLoading, error } = useIntegrations();
  const qc = useQueryClient();
  const [authStatus, setAuthStatus] = useState<
    null | "in_progress" | "failed"
  >(null);

  // Listen for the OAuth popup signaling completion via postMessage.
  // The popup (rendered by the server's /oauth/google/callback) posts
  // `{ type: 'thynra-oauth', provider, status }` to its opener and
  // auto-closes. We invalidate the integrations query so the UI re-renders
  // with the new connected state.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      // Hard origin check — only accept messages from our own dashboard's origin.
      if (e.origin !== window.location.origin) return;
      const data = e.data;
      if (
        data &&
        typeof data === "object" &&
        data.type === "thynra-oauth"
      ) {
        if (data.status === "success") {
          qc.invalidateQueries({ queryKey: ["tenant", "integrations"] });
          qc.invalidateQueries({ queryKey: ["tenant", "overview"] });
          setAuthStatus(null);
        } else {
          setAuthStatus("failed");
        }
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [qc]);

  const connectGoogle = useMutation({
    mutationFn: async () => {
      const { url } = await apiFetch<{ url: string }>(
        "/api/tenant/integrations/google/connect-url",
        { method: "POST" },
      );
      // Open as a popup so the dashboard tab stays put. Google's consent
      // screen requires a full browser context (cookies, redirects), so we
      // can't use an iframe — popup is the standard pattern.
      const popup = window.open(
        url,
        "thynra-oauth-google",
        "width=560,height=720,menubar=no,toolbar=no,location=yes",
      );
      if (!popup) {
        // Popup blocked — fall back to full-page nav so the flow still works.
        window.location.href = url;
        return;
      }
      setAuthStatus("in_progress");
      // If the user manually closes the popup before completing, reset state
      // so the button isn't stuck in "connecting…" forever.
      const interval = setInterval(() => {
        if (popup.closed) {
          clearInterval(interval);
          setAuthStatus((s) => (s === "in_progress" ? null : s));
        }
      }, 500);
    },
  });

  const disconnectGoogle = useMutation({
    mutationFn: () =>
      apiFetch("/api/tenant/integrations/google/disconnect", { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant", "integrations"] });
      qc.invalidateQueries({ queryKey: ["tenant", "overview"] });
    },
  });

  if (isLoading) return <LoadingRow />;
  if (error) return <ErrorBanner message={(error as Error).message} />;
  if (!data) return null;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Integrations</h1>
        <p className="mt-1 text-sm text-slate-500">
          External services connected to your AI receptionist.
        </p>
      </div>

      <div className="space-y-4">
        {/* Google Calendar */}
        <Card
          title="Google Calendar"
          subtitle="The receptionist places real bookings on this calendar and checks availability against your existing events."
          status={data.google_calendar.connected ? "connected" : "not_connected"}
          warning={
            !data.google_calendar.configured_on_platform
              ? "Google OAuth is not configured on the platform. Operator must set GOOGLE_OAUTH_CLIENT_ID/SECRET in fly secrets first."
              : undefined
          }
        >
          {data.google_calendar.connected ? (
            <div className="space-y-3">
              <div className="text-sm text-slate-600">
                Connected as{" "}
                <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">
                  {data.google_calendar.connected_email ?? "(unknown)"}
                </code>
                {data.google_calendar.connected_at && (
                  <>
                    {" "}
                    · since{" "}
                    {new Date(
                      data.google_calendar.connected_at,
                    ).toLocaleDateString()}
                  </>
                )}
              </div>
              <button
                onClick={() => {
                  if (
                    confirm(
                      "Disconnect Google Calendar? The receptionist will stop creating real calendar events until you reconnect.",
                    )
                  ) {
                    disconnectGoogle.mutate();
                  }
                }}
                disabled={disconnectGoogle.isPending}
                className="text-xs font-medium text-red-700 hover:underline disabled:opacity-50"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <button
                onClick={() => connectGoogle.mutate()}
                disabled={
                  connectGoogle.isPending ||
                  authStatus === "in_progress" ||
                  !data.google_calendar.configured_on_platform
                }
                className="px-4 py-2 text-sm font-medium rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 transition"
              >
                {authStatus === "in_progress"
                  ? "Waiting for Google…"
                  : connectGoogle.isPending
                    ? "Opening…"
                    : "Connect Google Calendar"}
              </button>
              {authStatus === "failed" && (
                <p className="text-xs text-red-700">
                  Connection didn't complete. Try again or check the popup
                  for the specific error.
                </p>
              )}
            </div>
          )}
        </Card>

        {/* Resend (email) */}
        <Card
          title="Email — Resend"
          subtitle="Used for booking confirmations, lead notifications, handoff alerts, and the magic-link sign-in flow."
          status={data.resend.from_email ? "connected" : "not_configured"}
          warning={
            !data.resend.configured_on_platform
              ? "Resend is not configured on the platform — set RESEND_API_KEY in fly secrets."
              : undefined
          }
        >
          <dl className="text-sm space-y-1">
            <Field label="From" value={data.resend.from_email} />
            <Field label="From name" value={data.resend.from_name} />
            <Field label="Reply-to" value={data.resend.reply_to_email} />
          </dl>
          {!data.resend.from_email && (
            <p className="mt-3 text-xs text-slate-500">
              The operator configures the verified sending domain during
              onboarding (see ONBOARDING.md step 2).
            </p>
          )}
        </Card>

        {/* Twilio (voice + SMS) */}
        <Card
          title="Twilio — voice & SMS"
          subtitle="Inbound phone number that routes to the AI receptionist."
          status={data.twilio.phone_number ? "connected" : "not_configured"}
          warning={
            !data.twilio.configured_on_platform
              ? "Twilio is not configured on the platform — set TWILIO_* in fly secrets."
              : undefined
          }
        >
          <dl className="text-sm">
            <Field label="DID" value={data.twilio.phone_number} />
          </dl>
        </Card>

        {/* Meta WhatsApp */}
        <Card
          title="WhatsApp — Meta Cloud API"
          subtitle="Direct integration with Meta's WhatsApp Business platform."
          status={
            data.meta_whatsapp.whatsapp_number ? "connected" : "not_configured"
          }
          warning={
            !data.meta_whatsapp.configured_on_platform
              ? "Meta WhatsApp Cloud API is not configured on the platform yet — see docs/META_CLOUD_API_SETUP.md."
              : undefined
          }
        >
          <dl className="text-sm">
            <Field
              label="WhatsApp number"
              value={data.meta_whatsapp.whatsapp_number}
            />
          </dl>
        </Card>
      </div>
    </div>
  );
}

function Card({
  title,
  subtitle,
  status,
  warning,
  children,
}: {
  title: string;
  subtitle: string;
  status: "connected" | "not_connected" | "not_configured";
  warning?: string;
  children: React.ReactNode;
}) {
  const statusBadge =
    status === "connected"
      ? { label: "✓ Connected", cls: "bg-emerald-100 text-emerald-700" }
      : status === "not_connected"
        ? { label: "○ Not connected", cls: "bg-slate-100 text-slate-600" }
        : { label: "Not configured", cls: "bg-slate-100 text-slate-500" };
  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5">
      <header className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-semibold text-slate-900">{title}</h2>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        </div>
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusBadge.cls}`}
        >
          {statusBadge.label}
        </span>
      </header>
      {warning && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
          ⚠ {warning}
        </div>
      )}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex">
      <dt className="text-slate-500 w-24 shrink-0">{label}</dt>
      <dd className="text-slate-700">
        {value ?? <span className="text-slate-400">not set</span>}
      </dd>
    </div>
  );
}
